import { getCell } from "@/grid/data/assemble-grid";
import type { DepartmentDefinition, GridTeam } from "@/grid/core/types";
import {
  DEFAULT_THRESHOLDS,
  getCellStatusForPeriod,
  resolveActual,
  type StatusColor,
  type StatusThresholds,
} from "@/grid/lib/pacing";

export const STATUS_POINTS: Record<StatusColor, number> = {
  green: 5,
  yellow: 3,
  red: 1,
};
export const RED_NO_ACTIVITY_POINTS = 0;

export interface StatusEntry {
  status: StatusColor;
  actual: number;
}

export const GRADE_PERCENTAGE_THRESHOLDS: { grade: string; minPercentage: number }[] = [
  { grade: "A+", minPercentage: 100 },
  { grade: "A", minPercentage: 92 },
  { grade: "A-", minPercentage: 83 },
  { grade: "B+", minPercentage: 75 },
  { grade: "B", minPercentage: 67 },
  { grade: "B-", minPercentage: 58 },
  { grade: "C+", minPercentage: 50 },
  { grade: "C", minPercentage: 42 },
  { grade: "C-", minPercentage: 33 },
  { grade: "D+", minPercentage: 25 },
  { grade: "D", minPercentage: 17 },
  { grade: "D-", minPercentage: 8 },
  { grade: "F", minPercentage: 0 },
];

export function getPointsForStatus(status: StatusColor, actual: number): number {
  if (status === "red") {
    return actual > 0 ? STATUS_POINTS.red : RED_NO_ACTIVITY_POINTS;
  }
  return STATUS_POINTS[status];
}

export function getGradeFromPercentage(percentage: number): string {
  for (const { grade, minPercentage } of GRADE_PERCENTAGE_THRESHOLDS) {
    if (percentage >= minPercentage) return grade;
  }
  return "F";
}

export function getOrdinal(n: number): string {
  const suffixes = ["th", "st", "nd", "rd"];
  const mod100 = n % 100;
  return n + (suffixes[(mod100 - 20) % 10] || suffixes[mod100] || suffixes[0]);
}

function thresholdsFor(department: DepartmentDefinition): StatusThresholds {
  return department.thresholds ?? DEFAULT_THRESHOLDS;
}

function collectStatusEntries(team: GridTeam, department: DepartmentDefinition): StatusEntry[] {
  const thresholds = thresholdsFor(department);
  const entries: StatusEntry[] = [];

  for (const metric of department.metrics) {
    for (const period of department.periods) {
      const cell = getCell(team, metric.id, period.id);
      const actual = resolveActual(cell?.actual);
      const status = getCellStatusForPeriod(
        period.id,
        cell?.actual ?? null,
        cell?.target ?? null,
        cell?.pacingTarget ?? null,
        thresholds,
      );
      entries.push({ status, actual });
    }
  }

  return entries;
}

export function calculateTeamGrade(team: GridTeam, department: DepartmentDefinition): string {
  const entries = collectStatusEntries(team, department);
  const periodCount = department.periods.length;
  const periodWeights = department.periods.map(
    (period) => department.periodWeights?.[period.id] ?? 1,
  );

  let score = 0;
  let maxScore = 0;

  department.metrics.forEach((metric, metricIndex) => {
    const metricWeight = metric.weight ?? 1;

    for (let periodIndex = 0; periodIndex < periodCount; periodIndex++) {
      const periodWeight = periodWeights[periodIndex] ?? 1;
      const combinedWeight = metricWeight * periodWeight;
      maxScore += STATUS_POINTS.green * combinedWeight;

      const entryIndex = metricIndex * periodCount + periodIndex;
      const entry = entries[entryIndex];
      if (entry) {
        score += getPointsForStatus(entry.status, entry.actual) * combinedWeight;
      }
    }
  });

  const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0;
  return getGradeFromPercentage(percentage);
}

export function calculateTeamRawScore(team: GridTeam, department: DepartmentDefinition): number {
  const entries = collectStatusEntries(team, department);
  const periodCount = department.periods.length;
  const periodWeights = department.periods.map(
    (period) => department.periodWeights?.[period.id] ?? 1,
  );

  let score = 0;

  department.metrics.forEach((metric, metricIndex) => {
    const metricWeight = metric.weight ?? 1;

    for (let periodIndex = 0; periodIndex < periodCount; periodIndex++) {
      const periodWeight = periodWeights[periodIndex] ?? 1;
      const combinedWeight = metricWeight * periodWeight;
      const entryIndex = metricIndex * periodCount + periodIndex;
      const entry = entries[entryIndex];
      if (entry) {
        score += getPointsForStatus(entry.status, entry.actual) * combinedWeight;
      }
    }
  });

  return score;
}

/** Dense ranking: tied teams share a rank; next rank continues without gaps. */
export function calculateTeamRankings(
  teams: GridTeam[],
  department: DepartmentDefinition,
): Map<number, number> {
  const teamScores = teams.map((team) => ({
    id: team.id,
    score: calculateTeamRawScore(team, department),
  }));

  teamScores.sort((a, b) => b.score - a.score);

  const rankings = new Map<number, number>();
  let currentRank = 1;

  for (let index = 0; index < teamScores.length; index++) {
    if (index > 0 && teamScores[index].score === teamScores[index - 1].score) {
      rankings.set(teamScores[index].id, rankings.get(teamScores[index - 1].id)!);
    } else {
      rankings.set(teamScores[index].id, currentRank);
      currentRank++;
    }
  }

  return rankings;
}

export function sortTeamsByRank(teams: GridTeam[], rankings: Map<number, number>): GridTeam[] {
  return [...teams].sort((a, b) => {
    const rankA = rankings.get(a.id) ?? Number.POSITIVE_INFINITY;
    const rankB = rankings.get(b.id) ?? Number.POSITIVE_INFINITY;
    return rankA - rankB;
  });
}
